import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getServices } from '../services'
import { AuthStep } from '@/ui/services/AuthService'

interface SignInArgs {
  username: string
  password: string
}

interface SignUpArgs {
  username: string
  password: string
}

interface VerifyArgs {
  username: string
  code: string
}

export interface AuthenticationState {
  userId?: string
  isSignedIn?: boolean
}

export interface UseAuthenticationReturn extends AuthenticationState {
  loadingAuthState: boolean
  checkAuthStateError: boolean
  signIn: (args: SignInArgs) => Promise<AuthStep>
  signUp: (args: SignUpArgs) => Promise<AuthStep>
  verify: (args: VerifyArgs) => Promise<AuthStep>
  signOut: () => Promise<void>
}

const authenticationKey = ['authenticate']

export const useAuthenticationState = () => {
  const { auth } = getServices()
  const queryClient = useQueryClient()

  const {
    data: isSignedInState,
    isLoading: loadingAuthState,
    error: authError,
    refetch: getAuthStatus,
  } = useQuery({
    queryKey: authenticationKey,
    queryFn: async () => {
      const state = deriveAuthenticationState()
      return state
    },
  })

  const signIn = useCallback(
    async (args: SignInArgs) => {
      const result = await auth.login(args)
      await getAuthStatus()
      return result
    },
    [auth, getAuthStatus],
  )

  const signUp = useCallback(
    async (args: SignInArgs) => {
      const result = await auth.signup(args)
      await getAuthStatus()
      return result
    },
    [auth, getAuthStatus],
  )

  const verify = useCallback(
    async (args: VerifyArgs) => {
      const result = await auth.verify(args)
      await getAuthStatus()
      return result
    },
    [auth, getAuthStatus],
  )

  const signOut = useCallback(async () => {
    try {
      await auth.logout()
      const state = deriveAuthenticationState()
      queryClient.setQueryData(authenticationKey, state)
    } catch (err) {
      console.error(err)
    }
  }, [auth, queryClient])

  const authenticationReturn: UseAuthenticationReturn = useMemo(
    () => ({
      ...isSignedInState,
      isSignedIn: isSignedInState?.isSignedIn,
      loadingAuthState: loadingAuthState,
      checkAuthStateError: !!authError,
      signIn,
      signUp,
      signOut,
      verify,
    }),
    [
      authError,
      isSignedInState,
      loadingAuthState,
      signIn,
      signUp,
      signOut,
      verify,
    ],
  )
  return authenticationReturn
}

async function deriveAuthenticationState(): Promise<AuthenticationState> {
  const { auth } = getServices()
  try {
    const user = await auth.getUser()
    const state = {
      userId: user,
      isSignedIn: user ? true : false,
    }
    return state
  } catch {
    return {
      isSignedIn: false,
    }
  }
}
